<template>
  <div class="style-spline-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item v-if="showAdvancedSections" title="现有几何关系" name="relations">
        <SelectionListFrame
          :items="existingRelations"
          selection-behavior="ctrl-multiple"
          list-role="list"
          :visible-rows="4"
          context-menu-enabled
          :is-item-delete-enabled="isSketchRelationSnapshotDeleteEnabled"
          :show-delete-all-action="existingRelations.some(isSketchRelationSnapshotDeleteEnabled)"
          item-key-field="relationId"
          @delete-item="handleDeleteRelationSelection"
          @delete-all="handleDeleteAllRelations"
        >
          <template #item="{ item }">
            <SketchRelationDiagnosticText
              :item="item"
              :label="resolveRelationLabel(item)"
            />
          </template>
        </SelectionListFrame>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="添加几何关系" name="add-relations">
        <n-space size="small">
          <n-button size="small" quaternary>固定</n-button>
        </n-space>
      </n-collapse-item>

      <n-collapse-item :title="typeSectionTitle" name="type">
        <template v-if="showTypeRadio">
          <n-radio-group
            :value="modeValue"
            :disabled="!canEditType"
            @update:value="handleModeChange"
          >
            <n-space vertical size="small">
              <n-radio value="bezier">贝塞尔</n-radio>
              <n-radio value="bspline-3">B-样条：度数 3</n-radio>
              <n-radio value="bspline-5">B-样条：度数 5</n-radio>
              <n-radio value="bspline-7">B-样条：度数 7</n-radio>
            </n-space>
          </n-radio-group>
        </template>
        <template v-else>
          <div class="param-grid">
            <div class="param-row">
              <n-text depth="3" class="param-label">曲线类型</n-text>
              <n-select
                size="small"
                :value="modeValue"
                :options="curveTypeOptions"
                :disabled="!canEditType"
                @update:value="handleCurveTypeChange"
              />
            </div>
            <div v-if="isBezierMode" class="param-row">
              <n-text depth="3" class="param-label">曲线度数</n-text>
              <n-input-number
                :value="bezierDegreeValue"
                size="small"
                :disabled="!canEditType"
                :show-button="true"
                :precision="0"
                :step="1"
                :min="2"
                :max="60"
                @update:value="handleBezierDegreeChange"
              />
            </div>
            <div v-else class="param-row">
              <n-text depth="3" class="param-label">控制顶点数</n-text>
              <n-input-number
                :value="controlVertexCount"
                size="small"
                :disabled="true"
                :show-button="false"
                :precision="0"
              />
            </div>
          </div>
        </template>
      </n-collapse-item>

      <n-collapse-item v-if="showOptions" title="选项" name="options">
        <div class="option-list">
          <n-checkbox
            :checked="constructionChecked"
            :disabled="!canEditOptions"
            @update:checked="handleConstructionToggle"
          >
            作为构造线
          </n-checkbox>
          <n-checkbox
            :checked="options.showCurvature"
            :disabled="true"
            @update:checked="handleShowCurvature"
          >
            显示曲率
          </n-checkbox>
          <n-checkbox
            :checked="options.keepInternalContinuity"
            :disabled="!canEditOptions"
            @update:checked="handleKeepInternalContinuity"
          >
            保持内部连续性
          </n-checkbox>
        </div>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="参数" name="parameters">
        <div class="param-grid">
          <div class="param-group">
            <n-radio-group
              :value="rationalValue"
              :disabled="!canEditParameters"
              @update:value="handleRationalChange"
            >
              <n-space vertical size="small">
                <n-radio :value="false">非理性样条曲线</n-radio>
                <n-radio :value="true">理性样条曲线</n-radio>
              </n-space>
            </n-radio-group>
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">点</n-text>
            <n-input-number
              :value="pointIndexValue"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="0"
              :step="1"
              :min="1"
              :max="maxPointIndex"
              @update:value="handlePointIndexChange"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">X</n-text>
            <n-input-number
              :value="pointXValue"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handlePointAxisChange('x', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Y</n-text>
            <n-input-number
              :value="pointYValue"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handlePointAxisChange('y', value)"
            />
          </div>
          <div v-if="rationalValue" class="param-group">
            <n-text depth="3" class="weight-label">控制顶点重量</n-text>
            <div class="weight-row">
              <n-input-number
                :value="weightValue"
                size="small"
                :disabled="!canEditParameters"
                :show-button="true"
                :precision="3"
                :step="1"
                :min="0.001"
                @update:value="handleWeightChange"
              />
            </div>
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NRadio,
  NRadioGroup,
  NSelect,
  NSpace,
  NText,
  useDialog,
} from 'naive-ui';
import type {
  PropertyManagerContext,
  StyleSplineOptions,
  StyleSplineParameterUpdate,
} from '@/core';
import { StyleSplineMode } from '@/core';
import { useDrawingStore, useViewer } from '@/hooks';
import { useGetSelectStyleSpline } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import SketchRelationDiagnosticText from '../common/SketchRelationDiagnosticText.vue';
import {isSketchRelationSnapshotDeleteEnabled} from '@/utils/sketch/isSketchRelationSnapshotDeleteEnabled';
import {
  isFiniteNullableNumber,
  isSketchStyleSplineObject,
  roundNullableToEightDecimalPlaces,
  SketchDrawingTool,
  type SketchStyleSplineObject,
  //------ 统一单位转换 开始------
  convertMmToM,
  convertMToMm,
  roundToEightDecimalPlaces,
  //------ 统一单位转换 结束------
} from "@puppycad/cad-core";
import type { SketchRelationSnapshot } from '@puppycad/cad-core';

defineOptions({
  inheritAttrs: false,
});
const dialog = useDialog();

interface StyleSplinePropertySectionProps {
  /** 属性面板上下文，用于统一提交草图属性编辑。 */
  context?: PropertyManagerContext;
}

const props = defineProps<StyleSplinePropertySectionProps>();

const defaultOptions: StyleSplineOptions = {
  mode: StyleSplineMode.Bezier,
  forConstruction: false,
  showCurvature: false,
  keepInternalContinuity: false,
};

const { getViewer } = useViewer();
const drawingStore = useDrawingStore();
const { currentDrawingTool, isDrawingMode } = storeToRefs(drawingStore);
const { getSelected: getSelectedStyleSpline, onChanged: onStyleSplineChanged } = useGetSelectStyleSpline();

const localStyleSplineOptions = ref<StyleSplineOptions>({ ...defaultOptions });
/** 当前选中的样式样条对象，参考直线属性面板使用浅引用保存运行时对象。 */
const selectedStyleSpline = shallowRef<SketchStyleSplineObject | null>(null);
/** 属性编辑刷新版本，用于驱动非响应式样式样条对象变更后刷新面板计算值。 */
const propertyEditRevision = ref(0);

/** 是否有样式样条被选中。 */
const isStyleSplineSelected = ref(false);
/** “作为构造线”勾选状态镜像，避免直接依赖非响应式草图对象字段。 */
const constructionCheckedValue = ref(false);
/** 当前选中样式样条曲线的现有关系快照列表。 */
const existingRelations = ref<SketchRelationSnapshot[]>([]);

//------ 样式样条构造线勾选状态同步 开始------
/**
 * 同步“作为构造线”勾选状态。
 *
 * 设计说明：
 * 样式样条是 cad-core 的运行时对象，不能交给 Vue 响应式系统代理。
 * 因此属性面板使用 boolean 镜像承接勾选回显，并在选择变化或用户操作后显式同步。
 *
 * @param spline 当前选中的样式样条对象。
 */
function syncConstructionCheckedState(spline: ReturnType<typeof getSelectedStyleSpline>): void {
  if (spline) {
    constructionCheckedValue.value = Boolean(spline.isConstruction);
    return;
  }
  constructionCheckedValue.value = Boolean(localStyleSplineOptions.value.forConstruction);
}
//------ 样式样条构造线勾选状态同步 结束------

const isInsertMode = computed(
  () =>
    Boolean(isDrawingMode.value)
    && currentDrawingTool.value === SketchDrawingTool.StyleSpline
    && !isStyleSplineSelected.value
);
const showAdvancedSections = computed(() => isStyleSplineSelected.value);
const canEditOptions = computed(() => isStyleSplineSelected.value);
const canEditParameters = computed(() => isStyleSplineSelected.value && Boolean(getViewer()));
const canEditType = computed(() => {
  if (isInsertMode.value) {
    return Boolean(getViewer());
  }
  return isStyleSplineSelected.value && Boolean(getViewer());
});
const options = computed(() => localStyleSplineOptions.value);
const constructionChecked = computed(() => constructionCheckedValue.value);

const modeValue = ref<StyleSplineMode>(options.value.mode);
const rationalValue = ref(false);
const pointIndexValue = ref(1);
const pointXValue = ref<number | null>(null);
const pointYValue = ref<number | null>(null);
const weightValue = ref<number | null>(1);
const bezierDegreeValue = ref(3);

const isBezierMode = computed(() => modeValue.value === StyleSplineMode.Bezier);
const activeBsplineDegree = computed(() => {
  if (modeValue.value === StyleSplineMode.BSpline5) {
    return 5;
  }
  if (modeValue.value === StyleSplineMode.BSpline7) {
    return 7;
  }
  if (modeValue.value === StyleSplineMode.BSpline3) {
    return 3;
  }
  return null;
});
const controlVertexCount = computed(() => {
  if (!activeBsplineDegree.value) {
    return null;
  }
  if (pointCount.value > 0) {
    return pointCount.value;
  }
  const normalized = Number.isFinite(bezierDegreeValue.value)
    ? Math.round(bezierDegreeValue.value)
    : 3;
  return Math.min(60, Math.max(2, normalized)) + 1;
});
const curveTypeOptions = computed(() => {
  const options: Array<{ label: string; value: StyleSplineMode }> = [
    { label: '\u8d1d\u585e\u5c14\u66f2\u7ebf', value: StyleSplineMode.Bezier },
  ];
  const normalized = Number.isFinite(bezierDegreeValue.value)
    ? Math.round(bezierDegreeValue.value)
    : 3;
  const allowed: StyleSplineMode[] = [];
  if (normalized >= 3) {
    allowed.push(StyleSplineMode.BSpline3);
  }
  if (normalized >= 5) {
    allowed.push(StyleSplineMode.BSpline5);
  }
  if (normalized >= 7) {
    allowed.push(StyleSplineMode.BSpline7);
  }
  const current = modeValue.value;
  if (current !== StyleSplineMode.Bezier && !allowed.includes(current)) {
    allowed.push(current);
  }
  const addOption = (degree: number, mode: StyleSplineMode) => {
    if (!allowed.includes(mode)) {
      return;
    }
    options.push({ label: `B\u6837\u6761\uff1a\u5ea6\u6570${degree}`, value: mode });
  };
  addOption(3, StyleSplineMode.BSpline3);
  addOption(5, StyleSplineMode.BSpline5);
  addOption(7, StyleSplineMode.BSpline7);
  return options;
});

const rationalValueComputed = computed({
  get() {
    if (!isStyleSplineSelected.value) {
      return false;
    }
    return Boolean(selectedStyleSpline.value?.getCurveMetadata().isRational);
  },
  set(value: boolean) {
    if (!isStyleSplineSelected.value) {
      return;
    }
    if (!value) {
      const weightList = weights.value;
      const hasWeight = weightList.some((weight) => Math.abs(weight - 1) > 1e-6);
      if (hasWeight) {
        dialog.warning({
          title: '样条曲线 - 确认失去的重量',
          content:
            '您即将失去重量。关于样条曲线形状的重要信息。选择“是”将保留样条曲线更改后的形状并失去重量；选择“否”会将样条曲线返回到之前的形状并保留重量。',
          positiveText: '是',
          negativeText: '否',
          onPositiveClick: () => {
            const update: StyleSplineParameterUpdate = {
              type: 'rational',
              value: false,
              resetWeights: true,
            };
            void updateSelectedStyleSpline(update);
          },
        });
        return;
      }
    }
    const update: StyleSplineParameterUpdate = { type: 'rational', value, resetWeights: !value };
    void updateSelectedStyleSpline(update);
  },
});

void rationalValueComputed.value;

const showOptions = computed(() => isStyleSplineSelected.value);
const showTypeRadio = computed(() => isInsertMode.value);
const typeSectionTitle = computed(() => (showTypeRadio.value ? '样条曲线类型' : '曲线类型'));

const controlPoints2d = computed(() => {
  void propertyEditRevision.value;
  if (!isStyleSplineSelected.value) {
    return [] as Array<{ x: number; y: number }>;
  }
  const spline = selectedStyleSpline.value;
  if (!spline) {
    return [] as Array<{ x: number; y: number }>;
  }
  return spline.getControlPoints2D();
});

const pointCount = computed(() => controlPoints2d.value.length);
const maxPointIndex = computed(() => (pointCount.value > 0 ? pointCount.value : 1));

const weights = computed(() => {
  void propertyEditRevision.value;
  if (!isStyleSplineSelected.value) {
    return [] as number[];
  }
  const spline = selectedStyleSpline.value;
  const controlPoints = spline?.getControlPoints() ?? [];
  if (!controlPoints || controlPoints.length === 0) {
    return [] as number[];
  }
  const rawWeights = spline?.getCurveMetadata().weights;
  return controlPoints.map((_, index) => {
    const raw = rawWeights?.[index];
    if (!Number.isFinite(raw)) {
      return 1;
    }
    return Math.max(0.001, raw);
  });
});

const roundWeight = (value: number | null) => {
  if (!isFiniteNullableNumber(value)) {
    return null;
  }
  return Number(value.toFixed(3));
};

const syncActivePointValues = (): void => {
  const index = pointIndexValue.value - 1;
  const activePoint = controlPoints2d.value[index];
  if (!activePoint) {
    pointXValue.value = null;
    pointYValue.value = null;
    weightValue.value = 1;
    return;
  }
  //------ 统一单位转换 开始------
  // 从底层点对象读取的米级坐标转换为毫米
  pointXValue.value = activePoint.x !== null ? roundToEightDecimalPlaces(convertMToMm(activePoint.x)) : null;
  pointYValue.value = activePoint.y !== null ? roundToEightDecimalPlaces(convertMToMm(activePoint.y)) : null;
  //------ 统一单位转换 结束------
  const weight = weights.value[index] ?? 1;
  weightValue.value = roundWeight(weight);
};

const syncFromGeometry = (): void => {
  if (!isStyleSplineSelected.value) {
    return;
  }
  const spline = selectedStyleSpline.value;
  if (spline) {
    const metadata = spline.getCurveMetadata();
    if (metadata.styleSplineMode) {
      modeValue.value = metadata.styleSplineMode as StyleSplineMode;
    }
    rationalValue.value = Boolean(metadata.isRational);
    if (pointCount.value > 0) {
      bezierDegreeValue.value = Math.min(60, Math.max(2, pointCount.value - 1));
    } else {
      const rawBezierDegree = Number(metadata.degree);
      if (Number.isFinite(rawBezierDegree)) {
        bezierDegreeValue.value = Math.min(60, Math.max(2, Math.round(rawBezierDegree)));
      } else {
        bezierDegreeValue.value = 3;
      }
    }
  }
  if (pointCount.value <= 0) {
    pointIndexValue.value = 1;
    pointXValue.value = null;
    pointYValue.value = null;
    weightValue.value = 1;
    return;
  }
  if (pointIndexValue.value < 1 || pointIndexValue.value > pointCount.value) {
    pointIndexValue.value = 1;
  }
  syncActivePointValues();
};

//------ 样式样条选中订阅顺序修复 开始------
const styleSplineChangedSub = onStyleSplineChanged().subscribe((spline) => {
  selectedStyleSpline.value = isSketchStyleSplineObject(spline) ? spline : null;
  isStyleSplineSelected.value = selectedStyleSpline.value !== null;
  refreshExistingRelations(selectedStyleSpline.value);
  // 选中变化时同步参数，确保属性面板与底层绘制配置保持一致。
  const renderer = getViewer();
  localStyleSplineOptions.value = renderer?.getStyleSplineDrawingOptions?.() ?? { ...defaultOptions };
  syncConstructionCheckedState(selectedStyleSpline.value);
  // 切换选中对象时从第一个控制点开始展示，避免沿用上一次对象的越界索引。
  pointIndexValue.value = 1;
  if (isStyleSplineSelected.value) {
    syncFromGeometry();
  }
});
onBeforeUnmount(() => {
  styleSplineChangedSub.unsubscribe();
});
//------ 样式样条选中订阅顺序修复 结束------

watch(pointCount, (count) => {
  if (count <= 0) {
    pointIndexValue.value = 1;
    pointXValue.value = null;
    pointYValue.value = null;
    weightValue.value = 1;
    return;
  }
  const next = Math.min(Math.max(1, pointIndexValue.value), count);
  if (next !== pointIndexValue.value) {
    pointIndexValue.value = next;
  }
  syncActivePointValues();
});

watch([isStyleSplineSelected, controlPoints2d, weights], () => {
  if (isStyleSplineSelected.value) {
    syncFromGeometry();
  }
}, { immediate: true });

watch([isInsertMode, () => options.value.mode], ([isDrawing]) => {
  if (isDrawing) {
    modeValue.value = options.value.mode;
  }
}, { immediate: true });

const defaultExpanded = computed(() => {
  if (showAdvancedSections.value) {
    return ['relations', 'add-relations', 'options', 'type', 'parameters'];
  }
  return ['type'];
});

function updateOptions(patch: Partial<StyleSplineOptions>): void {
  const renderer = getViewer();
  const currentOptions = renderer?.getStyleSplineDrawingOptions?.();
  if (!renderer || !currentOptions) {
    return;
  }
  renderer.setStyleSplineDrawingMode?.(String(patch.mode ?? currentOptions.mode));
  localStyleSplineOptions.value = { ...localStyleSplineOptions.value, ...patch };
  syncConstructionCheckedState(getSelectedStyleSpline());
}


/**
 * 将样式样条属性编辑统一交给 PropertyManagerContext 收口。
 * @param spline 被编辑的样式样条对象。
 */
function finalizeStyleSplinePropertyEdit(spline: ReturnType<typeof getSelectedStyleSpline>): void {
  if (!spline) {
    return;
  }
  props.context?.finalizeSketchPropertyEdit?.(spline, {
    reason: 'property-edit',
    sourceObjectId: spline.uuid,
  });
}

async function updateSelectedStyleSpline(update: StyleSplineParameterUpdate): Promise<void> {
  const spline = selectedStyleSpline.value;
  if (!spline) {
    return;
  }
  try {
    if (update.type === 'point') {
      // X/Y 参数来自控制点局部坐标，提交时必须走 cad-core 的重建入口刷新曲线。
      await spline.rebuildByControlPointCoordinate(update.index, update.axis, update.value);
    } else if (update.type === 'rational') {
      const controlPointCount = spline.getControlPoints().length;
      const currentMetadata = spline.getCurveMetadata();
      const nextWeights = update.resetWeights
        ? new Array(controlPointCount).fill(1)
        : currentMetadata.weights.length === controlPointCount
          ? currentMetadata.weights
          : new Array(controlPointCount).fill(1);
      // 理性/非理性会改变曲线采样，必须通过重建入口刷新几何。
      await spline.rebuildByControlPointDefinition(spline.getControlPoints().map((point) => point.getPosition()), {
        isRational: update.value,
        weights: update.value ? nextWeights : new Array(controlPointCount).fill(1),
      });
      rationalValue.value = update.value;
    } else if (update.type === 'weight') {
      const controlPointCount = spline.getControlPoints().length;
      const currentMetadata = spline.getCurveMetadata();
      const nextWeights = currentMetadata.weights.length === controlPointCount
        ? [...currentMetadata.weights]
        : new Array(controlPointCount).fill(1);
      const weightIndex = Math.min(Math.max(0, Math.floor(update.index)), controlPointCount - 1);
      // 控制顶点重量会改变理性曲线形状，必须重建采样点而不是只写元数据。
      nextWeights[weightIndex] = Math.max(0.001, update.value);
      await spline.rebuildByControlPointDefinition(spline.getControlPoints().map((point) => point.getPosition()), {
        isRational: true,
        weights: nextWeights,
      });
      rationalValue.value = true;
      weightValue.value = roundWeight(nextWeights[weightIndex]);
    }
  } catch (error) {
    console.error('样式样条属性更新失败。', error);
    return;
  }
  finalizeStyleSplinePropertyEdit(spline);
  propertyEditRevision.value += 1;
  syncFromGeometry();
}

function applyMode(mode: StyleSplineMode): void {
  if (isStyleSplineSelected.value) {
    const update: StyleSplineParameterUpdate = { type: 'mode', mode };
    void updateSelectedStyleSpline(update);
    return;
  }
  const viewer = getViewer();
  if (viewer?.setStyleSplineDrawingMode) {
    viewer.setStyleSplineDrawingMode(String(mode));
    localStyleSplineOptions.value = {
      ...localStyleSplineOptions.value,
      mode,
    };
    return;
  }
  updateOptions({ mode });
}

function handleModeChange(mode: StyleSplineMode): void {
  modeValue.value = mode;
  applyMode(mode);
}

function handleCurveTypeChange(value: StyleSplineMode | null): void {
  if (!value) {
    return;
  }
  modeValue.value = value;
  applyMode(value);
}

function handleBezierDegreeChange(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  const normalized = Math.min(60, Math.max(2, Math.round(value)));
  bezierDegreeValue.value = normalized;
  if (isStyleSplineSelected.value) {
    const update: StyleSplineParameterUpdate = { type: 'bezierDegree', value: normalized };
    void updateSelectedStyleSpline(update);
  }
}

//------ 样式样条曲线现有几何关系 开始------

/**
 * 刷新当前选中样式样条曲线的现有关系列表。
 * @param spline 选中的样式样条曲线对象（null 表示无选中）。
 */
function refreshExistingRelations(spline: ReturnType<typeof getSelectedStyleSpline>): void {
  if (!spline) {
    existingRelations.value = [];
    return;
  }
  existingRelations.value = spline.constraints.getRelationSnapshots();
}

/**
 * 将关系快照转为显示标签。
 * @param item 列表项。
 * @returns 显示标签。
 */
function resolveRelationLabel(item: unknown): string {
  const snapshot = item as SketchRelationSnapshot | null;
  if (!snapshot) {
    return '';
  }
  return snapshot.baseDisplayName || snapshot.name || String(snapshot.relationType);
}

async function removeStyleSplineRelation(
  spline: NonNullable<ReturnType<typeof getSelectedStyleSpline>>,
  relationId: number,
): Promise<boolean> {
  return await props.context?.removeSketchRelation?.(spline, relationId) ?? false;
}

/**
 * 处理右键删除单个关系。
 * @param payload SelectionListFrame delete-item 回调参数。
 */
async function handleDeleteRelationSelection(payload: { item: unknown; index: number }): Promise<void> {
  const spline = getSelectedStyleSpline();
  if (!spline) {
    return;
  }
  const snapshot = payload.item as SketchRelationSnapshot;
  if (!snapshot || !Number.isFinite(snapshot.id) || !isSketchRelationSnapshotDeleteEnabled(snapshot)) {
    return;
  }
  if (!await removeStyleSplineRelation(spline, snapshot.id)) {
    return;
  }
  const viewer = getViewer();
  viewer?.removeObjectConstraintMarker?.(spline, snapshot.id);
  refreshExistingRelations(spline);
}

/**
 * 处理右键删除全部关系。
 */
async function handleDeleteAllRelations(): Promise<void> {
  const spline = getSelectedStyleSpline();
  if (!spline) {
    return;
  }
  const viewer = getViewer();
  for (const snapshot of existingRelations.value.filter(isSketchRelationSnapshotDeleteEnabled)) {
    if (!await removeStyleSplineRelation(spline, snapshot.id)) {
      return;
    }
    viewer?.removeObjectConstraintMarker?.(spline, snapshot.id);
  }
  refreshExistingRelations(spline);
}

//------ 样式样条曲线现有几何关系 结束------

function handleConstructionToggle(value: boolean): void {
  if (isStyleSplineSelected.value) {
    const selectedStyleSpline = getSelectedStyleSpline();
    if (selectedStyleSpline) {
      // 直接通过对象 setter 修改构造线状态（setter 内部切换材质）。
      selectedStyleSpline.isConstruction = value;
      const viewer = getViewer();
      viewer?.syncObjectBaseVisualState?.(selectedStyleSpline);
      if (value || !viewer?.shadedSketchContoursEnabled) {
        selectedStyleSpline.clearContourDisplay?.();
      } else if (selectedStyleSpline.canCreateContour?.()) {
        // 开链样式样条不能创建轮廓，恢复构造线状态时必须保持静默。
        selectedStyleSpline.syncContour?.();
      }
      constructionCheckedValue.value = value;
      finalizeStyleSplinePropertyEdit(selectedStyleSpline);
      syncConstructionCheckedState(selectedStyleSpline);
      return;
    }
  }
  updateOptions({ forConstruction: value });
  constructionCheckedValue.value = value;
}

function handleShowCurvature(value: boolean): void {
  updateOptions({ showCurvature: value });
}

function handleKeepInternalContinuity(value: boolean): void {
  updateOptions({ keepInternalContinuity: value });
}

function handleRationalChange(value: boolean): void {
  if (!isStyleSplineSelected.value) {
    return;
  }
  if (value === rationalValue.value) {
    return;
  }
  if (!value) {
    const weightList = weights.value;
    const hasWeight = weightList.some((weight) => Math.abs(weight - 1) > 1e-6);
    if (hasWeight) {
      dialog.warning({
        title: '样条曲线 - 确认失去的重量',
        content:
          '您即将失去重量。关于样条曲线形状的重要信息。选择“是”将保留样条曲线更改后的形状并失去重量；选择“否”会将样条曲线返回到之前的形状并保留重量。',
        positiveText: '是',
        negativeText: '否',
        onPositiveClick: () => {
          rationalValue.value = false;
          const update: StyleSplineParameterUpdate = {
            type: 'rational',
            value: false,
            resetWeights: true,
          };
          void updateSelectedStyleSpline(update);
        },
        onNegativeClick: () => {
          rationalValue.value = true;
        },
      });
      return;
    }
  }
  rationalValue.value = value;
  const update: StyleSplineParameterUpdate = { type: 'rational', value, resetWeights: !value };
  void updateSelectedStyleSpline(update);
}

function handlePointIndexChange(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  const count = maxPointIndex.value;
  const next = Math.min(Math.max(1, Math.round(value)), count);
  pointIndexValue.value = next;
  syncActivePointValues();
}

function handlePointAxisChange(axis: 'x' | 'y', value: number | null): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  if (axis === 'x') {
    pointXValue.value = value;
  } else {
    pointYValue.value = value;
  }
  const index = pointIndexValue.value - 1;
  //------ 统一单位转换 开始------
  // 用户输入的毫米值转换为底层所需的米级
  const update: StyleSplineParameterUpdate = { type: 'point', index, axis, value: convertMmToM(value) };
  //------ 统一单位转换 结束------
  void updateSelectedStyleSpline(update);
}

function handleWeightChange(value: number | null): void {
  if (!canEditParameters.value || !rationalValue.value || value === null || Number.isNaN(value)) {
    return;
  }
  const index = pointIndexValue.value - 1;
  const nextValue = Math.max(0.001, value);
  weightValue.value = nextValue;
  const update: StyleSplineParameterUpdate = { type: 'weight', index, value: nextValue };
  void updateSelectedStyleSpline(update);
}
</script>

<style scoped>
.style-spline-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-group {
  padding: 2px 0;
}

.param-row {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}

.weight-label {
  font-size: 12px;
  margin-bottom: 4px;
}

.weight-row {
  display: flex;
  align-items: center;
}
</style>
